import dynamic from 'next/dynamic';

// Static imports (not lazy-loaded)
import Hero from '@/components/hero/Hero';
import { InlineImage } from '@/components/baseImage/InlineImage';
import { Headline } from '@/components/headline/Headline';
import { Header } from '@/components/header/Header';
import { Footer } from '@/components/footer/Footer';
import { Link } from '@/components/link/Link';
import AccordionImageWrapper from '@/components/accordion-image-wrapper/AccordionImageWrapper';
import AdBanner from '@/components/ad-banner/AdBanner';
import { PageHeader } from '@/components/page-header/PageHeader';
import { IntroHeader } from '@/components/intro-header/IntroHeader';
import { ArticleHeader } from '@/components/article-header/ArticleHeader';
import { Checklist } from '@/components/checklist/Checklist';
import MultistepForm from '@/sections/forms/multistep-form/MultistepForm';

// Dynamic imports
const Timeline = dynamic(() =>
  import('@/components/timeline/Timeline').then((e) => e.Timeline),
);
const AccordionList = dynamic(() =>
  import('@/components/accordion/AccordionList').then((e) => e.AccordionList),
);
const Badge = dynamic(() => import('@/components/badge/Badge'));
const Card = dynamic(() => import('@/components/card/Card'));
const ImageTextRender = dynamic(() => import('@/components/image-text'));
const FactsFigures = dynamic(() =>
  import('@/components/facts-figures/FactsFigures').then((e) => e.FactsFigures),
);
const LogoCarousel = dynamic(() =>
  import('@/components/logo-carousel/LogoCarousel').then((e) => e.LogoCarousel),
);
const AsServices = dynamic(() =>
  import('@/components/as-services/AsServices').then((e) => e.AsServices),
);
const Tabs = dynamic(() =>
  import('@/components/tabs/Tabs').then((e) => e.Tabs),
);
const HeadlineWrapper = dynamic(() =>
  import('@/components/headline-wrapper/HeadlineWrapper').then(
    (e) => e.HeadlineWrapper,
  ),
);
const Breadcrumps = dynamic(() =>
  import('@/components/breadcrumps/Breadcrumps').then((e) => e.Breadcrumps),
);
const CardWrapperGrid = dynamic(
  () => import('@/components/cards-wrapper-grid/CardsWrapperGrid'),
);
const TextContainer = dynamic(() =>
  import('@/components/text-container/TextContainer').then(
    (e) => e.TextContainer,
  ),
);
const RichText = dynamic(() =>
  import('@/components/rich-text/RichText').then((e) => e.RichText),
);
const TwoImages = dynamic(() =>
  import('@/components/two-images/TwoImages').then((e) => e.TwoImages),
);
const CollectionCards = dynamic(() =>
  import('@/components/collection-cards/CollectionCards').then(
    (e) => e.CollectionCards,
  ),
);
const FeatureArticle = dynamic(() =>
  import('@/components/feature-article/FeatureArticle').then(
    (e) => e.FeatureArticle,
  ),
);
const TipBox = dynamic(() =>
  import('@/components/tip-box/TipBox').then((e) => e.TipBox),
);
const ImageContent = dynamic(() =>
  import('@/components/image-content/ImageContent').then((e) => e.ImageContent),
);
const ContentCards = dynamic(() =>
  import('@/components/content-cards/ContentCards').then((e) => e.ContentCards),
);
const Testimonials = dynamic(
  () => import('@/components/testimonals/Testimonals'),
);
const ScriptInjector = dynamic(
  () => import('@/components/script-injector/ScriptInjector'),
);
const CardsWrapper = dynamic(
  () => import('@/components/cards-wrapper/CardsWrapper'),
);
const ContactDetails = dynamic(
  () => import('@/components/contact-details/ContactDetails'),
);
const ModularContactDetails = dynamic(
  () => import('@/components/modular-contact-details/ModularContactDetails'),
);
const TeamCardsWrapper = dynamic(
  () => import('@/components/team-cards-wrapper/TeamCardsWrapper'),
);
const Masonry = dynamic(() =>
  import('@/components/masonry-grid/Masonry').then((e) => e.Masonry),
);
const Select = dynamic(() =>
  import('@/components/select/Select').then((e) => e.Select),
);
const Table = dynamic(() => import('@/components/table/Table'));
const LogoWrapper = dynamic(() =>
  import('@/components/logo-wrapper/LogoWrapper').then((e) => e.LogoWrapper),
);
const VideoPlayer = dynamic(() =>
  import('@/components/video-player/VideoPlayer').then((e) => e.VideoPlayer),
);
const NumberedList = dynamic(() =>
  import('@/components/numbered-list/NumberedList').then((e) => e.NumberedList),
);
const MiniSmzHub = dynamic(() => import('@/components/mini-smzHub/MiniSmzHub'));
const AdBannerCarousel = dynamic(
  () => import('@/components/ad-banner-carousel/AdBannerCarousel'),
);
const Schema = dynamic(() => import('@/components/schema/Schema'));
const Overlay = dynamic(() =>
  import('@/components/overlay/Overlay').then((e) => e.Overlay),
);
const Media = dynamic(() => import('@/components/media/Media'));
const LineChart = dynamic(() => import('@/components/line-chart/LineChart'));
const ChartWrapper = dynamic(
  () => import('@/components/chart-wrapper/ChartWrapper'),
);
const SearchMarketData = dynamic(
  () => import('@/components/search-market-data/SearchMarketData'),
);
const DataMarketTable = dynamic(
  () => import('@/components/data-market-table/DataMarketTable'),
);
const DataMarketWrapper = dynamic(
  () => import('@/components/data-market-wrapper/DataMarketWrapper'),
);
const RelatedCollection = dynamic(
  () => import('@/components/related-collection/RelatedCollection'),
);
const CTA = dynamic(() => import('@/components/cta/CTA'));
const ProgressiveForms = dynamic(
  () => import('@/sections/forms/progressive-form/ProgressiveForm'),
);
const GatewayForm = dynamic(
  () => import('@/components/gateway-forms/GatewayForm'),
);
const ModularGatewayForm = dynamic(
  () => import('@/components/modular-gateway-form/ModularGatewayForm'),
);
const PromoBlock = dynamic(() => import('@/components/promo-block/PromoBlock'));
const Gallery = dynamic(() =>
  import('@/components/gallery/Gallery').then((e) => e.Gallery),
);

const Swiper = dynamic(() => import('@/components/Swiper/Swiper'));
const FAQ = dynamic(() => import('@/components/faq/FAQ').then((e) => e.FAQ));
const TabbedContent = dynamic(
  () => import('@/sections/tabbed-content/TabbedContent'),
);
const VisualStrategy = dynamic(
  () => import('@/sections/visual-strategy/VisualStrategy'),
);
const CalculatorSection = dynamic(
  () => import('@/components/calculator-section/CalculatorSection'),
);
const TimelineCards = dynamic(() =>
  import('@/sections/timeline-cards/TimelineCards').then(
    (e) => e.TimelineCards,
  ),
);

// Component map
export const componentMap: Record<string, any> = {
  'elements.accordion-list': AccordionList,
  'elements.badge': Badge,
  'elements.link': Link,
  'elements.image': InlineImage,
  'elements.card': Card,
  'elements.headline': Headline,
  'sections.ad-banner': AdBanner,
  'sections.cta': CTA,
  'sections.hero': Hero,
  'sections.header': Header,
  'sections.footer': Footer,
  'sections.image-text': ImageTextRender,
  'sections.facts-figures': FactsFigures,
  'sections.logo-carousel': LogoCarousel,
  'sections.as-services': AsServices,
  'sections.card-wrapper': CardsWrapper,
  'elements.headline-wrapper': HeadlineWrapper,
  'sections.tabs': Tabs,
  'elements.breadrcrumbs': Breadcrumps,
  'elements.cards': Card,
  'elements.card-wrapper-grid': CardWrapperGrid,
  'elements.text-container': TextContainer,
  'sections.gateway-form': GatewayForm,
  'sections.modular-gateway-form': ModularGatewayForm,
  'elements.rich-text': RichText,
  'elements.two-images': TwoImages,
  'elements.intro-header': IntroHeader,
  'elements.content-cards': ContentCards,
  'sections.collection-cards': CollectionCards,
  'sections.page-header': PageHeader,
  'elements.feature-article': FeatureArticle,
  'elements.tip-box': TipBox,
  'elements.image-content': ImageContent,
  'sections.timeline': Timeline,
  'sections.testimonials': Testimonials,
  'sections.script-injector': ScriptInjector,
  'sections.team-cards-wrapper': TeamCardsWrapper,
  'sections.contact-details': ContactDetails,
  'sections.modular-contact-details': ModularContactDetails,
  'sections.masonry-grid': Masonry,
  'elements.select': Select,
  'sections.accordion-image-wrapper': AccordionImageWrapper,
  'elements.table': Table,
  'sections.logo-wrapper': LogoWrapper,
  'sections.article-header': ArticleHeader,
  'elements.video-player': VideoPlayer,
  'sections.numbered-list': NumberedList,
  'sections.mini-smz-hub': MiniSmzHub,
  'sections.ad-banner-carousel': AdBannerCarousel,
  'sections.schema': Schema,
  'sections.overlay': Overlay,
  'sections.media': Media,
  'sections.progressive-form': ProgressiveForms,
  'elements.line-chart': LineChart,
  'sections.chart-wrapper': ChartWrapper,
  'elements.search-market-data': SearchMarketData,
  'elements.data-market-table': DataMarketTable,
  'sections.data-market-wrapper': DataMarketWrapper,
  'sections.related-collection': RelatedCollection,
  'sections.promo-block': PromoBlock,
  'sections.gallery': Gallery,
  'elements.checklist': Checklist,
  'sections.swiper': Swiper,
  'sections.faq': FAQ,
  'sections.tabbed-content': TabbedContent,
  'sections.visual-strategy': VisualStrategy,
  'sections.user-calculator': CalculatorSection,
  'sections.timeline-cards': TimelineCards,
  'forms.multistep-form': MultistepForm,
};
